<script setup>
import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

const props = defineProps({
  // [{ lat, lng, label }]
  points: { type: Array, default: () => [] },
});

// Fix Leaflet default icon URLs for the Vite bundler.
// eslint-disable-next-line no-underscore-dangle
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

const DEFAULT_CENTER = [20.5888, -100.3899]; // Querétaro
const DEFAULT_ZOOM = 12;

const mapContainer = ref(null);
let map = null;
let layer = null;

const validPoints = () =>
  props.points
    .map(point => ({
      lat: parseFloat(point.lat),
      lng: parseFloat(point.lng),
      label: point.label,
    }))
    .filter(point => !Number.isNaN(point.lat) && !Number.isNaN(point.lng));

const renderMarkers = () => {
  if (!map) return;
  if (layer) layer.remove();
  layer = L.layerGroup().addTo(map);

  const points = validPoints();
  points.forEach(point => {
    const marker = L.marker([point.lat, point.lng]).addTo(layer);
    if (point.label) marker.bindPopup(point.label);
  });

  if (points.length === 1) {
    map.setView([points[0].lat, points[0].lng], 15);
  } else if (points.length > 1) {
    map.fitBounds(points.map(point => [point.lat, point.lng]), {
      padding: [30, 30],
      maxZoom: 16,
    });
  }
};

watch(() => props.points, renderMarkers, { deep: true });

onMounted(() => {
  nextTick(() => {
    map = L.map(mapContainer.value).setView(DEFAULT_CENTER, DEFAULT_ZOOM);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);
    renderMarkers();
    setTimeout(() => map.invalidateSize(), 300);
  });
});

onUnmounted(() => {
  if (map) map.remove();
});
</script>

<template>
  <div ref="mapContainer" class="h-[360px] w-full rounded-lg z-0" />
</template>
